/*
 * @Author: Json Chen
 * @Date: 2021-07-26 12:52:13
 * @LastEditTime: 2022-06-13 17:56:57
 * @LastEditors: Json Chen
 * @Description:
 * @FilePath: /unifyxp-v2-store-portal/src/pages/SalesCannelVersion/SalesChannelSettings/SalesChannelLogistics/Components/PickupLocations.tsx
 */

import EditDraggableTable from '@/src/components/DroppableTable/EditDraggableTable';
import {
  EditIcon,
  FrontTitle,
} from '@/src/components/EditableTable/EditableTable';
import { weekList } from '@/src/pages/Settings/Logistics/PickupLocations/Components/config';
import { Form } from 'antd';
import { get } from 'lodash';
import React, { useMemo } from 'react';
import { SizeFittingTable } from 'src/components/Antd';
import { TableInput } from 'src/components/EditableTable/TableForm/TableForm';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet, replaceI18nVariables } from 'src/tools/tools';
import styles from '../style.module.less';

const calcTimeShow = (data, _weekList, getI18n) => {
  switch (data.type) {
    case 'All':
      return <div>{getI18n('salesChannelLogistics.index.24hours7days')}</div>;
    case 'Customize':
      const customTimes = (data.customizes || []).reduce((p, c, index) => {
        if (!c.checked) {
          return p;
        }
        const week = _weekList[index]?.name;

        if (index === 0) {
          return p.concat({
            text: week,
            range: c.range,
          });
        }
        const sameIndex = p.findIndex(
          (i) => i.range[0] === c.range[0] && i.range[1] === c.range[1],
        );

        if (sameIndex > -1) {
          return p.map((i, idx) => {
            return idx === sameIndex
              ? {
                  ...i,
                  text: (i.text || '').split(' - ')[0] + ' - ' + week,
                }
              : i;
          });
        }
        return p.concat({
          text: week,
          range: c.range,
        });
      }, []);
      return customTimes.map((item, index) => (
        <div key={index}>
          {item.text} / {item.range[0]} - {item.range[1]}
        </div>
      ));
  }
};

const columns = (disableEdit, { getI18n }) => [
  {
    title: getI18n('salesChannelLogistics.index.pickupLocation'),
    default: true,
    minWidth: 216,
    childGenerate: (_, record) => {
      return (
        <div>
          <div className='checkboxTitle'>{record.locationName}</div>
          <div className={styles.pickupLocationDescription}>
            <div>
              {get(record, 'addressLine', '')}
              {get(record, 'province.name', '')}
              {(get(record, 'country.name'), '')} {get(record, 'postcode', '')}
            </div>
            <div>
              +{record.areaCode} {record.phone}
            </div>
          </div>
        </div>
      );
    },
  },
  {
    title: (
      <FrontTitle>
        <EditIcon />
        <span>
          {getI18n('salesChannelLogistics.index.pickupLocationTitle')}
        </span>
      </FrontTitle>
    ),
    dataIndex: 'title',
    key: 'title',
    default: true,
    minWidth: 280,
    childGenerate: (_, record, index) => {
      return (
        <Form.Item name={['pickupLocations', index, 'title']}>
          <TableInput disabled={disableEdit} />
        </Form.Item>
      );
    },
  },
  {
    title: (
      <FrontTitle>
        <EditIcon />
        <span>
          {getI18n('salesChannelLogistics.index.pickupLocationAddress')}
        </span>
      </FrontTitle>
    ),
    dataIndex: 'address',
    key: 'address',
    default: true,
    minWidth: 329,
    childGenerate: (_, record, index) => {
      return (
        <Form.Item name={['pickupLocations', index, 'address']}>
          <TableInput disabled={disableEdit} />
        </Form.Item>
      );
    },
  },

  {
    title: getI18n('salesChannelLogistics.index.availableTime'),
    default: true,
    minWidth: 228,
    childGenerate: (_, record) => {
      const _weekList = weekList({ getI18n });
      return (
        <div>
          {calcTimeShow(record.pickupAvailableTime, _weekList, getI18n)}
        </div>
      );
    },
  },
];

const PickupLocations = ({
  value = [],
  onChange,
  isPrimary,
  disableEdit,
}: any) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  /*
     value 转换后的列表 
      [
        {
          uuid - shippingZone的uuid ,
          name - shippingZone的name,
          table:[
            shippingRate
          ]
      }
    ]
  
  */
  const list = useMemo(() => {
    return (value || []).reduce((p, c) => {
      return p.concat({
        ...c.pickupLocation,
        checked: c.checked,
        dragable: c.checked,
        hideDragHandler: !c.checked,
      });
    }, []);
  }, [value]);

  const rowSelection = {
    selectedRowKeys: list.reduce((p, c) => {
      return c.checked ? p.concat(c.uuid) : p;
    }, []),
    getCheckboxProps: (record) => ({
      disabled: !isPrimary || disableEdit,
    }),
    onChange: (selectedList) => {
      onChange(
        value.map((item) => {
          return {
            ...item,
            checked: selectedList.includes(item.pickupLocation.uuid),
          };
        }),
      );
    },
  };

  // 计算某一分类是否有勾选且未全选
  const onTableChange = (newList) => {
    onChange(
      newList.map((i) => value.find((v) => v.pickupLocation.uuid === i.uuid)),
    );
  };

  const TableProps = {
    id: 'LogisticPickupLocations',
    rowKey: 'uuid',
    dataSource: list,
    columns: columns(disableEdit, { getI18n }),
    rowSelection: rowSelection,
    onTableChange: onTableChange,
    className: styles.table,
    rowClassName: styles.row,
    pagination: false,
    hideAllDragHandlers: disableEdit,
  };

  return (
    <div>
      <div className={styles.pickupLocationsSelected}>
        {replaceI18nVariables(
          getI18n('salesChannelLogistics.index.pickupLocationsSelected'),
          (value || []).filter((i) => i.checked).length,
          list.length,
        )}
      </div>

      {isPrimary ? (
        <EditDraggableTable {...TableProps} />
      ) : (
        <SizeFittingTable {...TableProps} />
      )}
    </div>
  );
};

export default PickupLocations;
